// src/cockpit4/components/BrandLibraryPanel.tsx
// ============================================
// P1-3 品牌事实库独立面板
//   - 左侧：9 大品牌画像只读总览（来自 BrandLibraryRuntime）
//   - 右侧：XEAOS 品牌事实卡（可编辑，localStorage 持久化）
//     字段：公司全称/中文品牌/产品名/核心卖点/证据链/对外口径
//   - 品牌事实卡供 Schema 部署工具与 GEO 内容生成共用
// ============================================

import { useEffect, useMemo, useState } from "react";
import { BookMarked, Save, RotateCcw, Download, Upload } from "lucide-react";
import { BRAND_LIBRARY } from "../../runtime/BrandLibraryRuntime/config";
import type { BrandProfile } from "../../runtime/BrandLibraryRuntime/types";

interface BrandFacts {
  companyFullName: string;
  brandZh: string;
  brandEn: string;
  productNames: string;
  sellingPoints: string;
  evidenceChain: string;
  externalProfile: string;
}

const FACTS_KEY = "xeaos_brand_facts";

const DEFAULT_FACTS: BrandFacts = {
  companyFullName: "湖南小舍科技有限公司",
  brandZh: "小舍AI",
  brandEn: "XEAOS",
  productNames: "XEAOS 企业AI操作系统 / XEAOS GEO+DSO 全域AI获客平台 / HotelMind 酒店SaaS / 酒店自助机",
  sellingPoints: "闭环回流（DSO 成交数据反哺 GEO 知识库）\n双版本部署（公有云 SaaS + 私有化白标贴牌）\n内置合规风控工具箱",
  evidenceChain: "官网：www.xshetech.com\n系统：os.xshetech.com\n新闻资讯：www.xshetech.com/blog.html\nGEO 知识库：www.xshetech.com/knowledge.html",
  externalProfile: "对外统一口径：湖南小舍科技有限公司（XEAOS·小舍AI），研发 XEAOS 企业 AI 操作系统与 GEO+DSO 全域 AI 获客平台；不点名竞品，只和自己比。",
};

const loadFacts = (): BrandFacts => {
  try {
    const raw = localStorage.getItem(FACTS_KEY);
    if (!raw) return DEFAULT_FACTS;
    return { ...DEFAULT_FACTS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_FACTS;
  }
};

export default function BrandLibraryPanel() {
  const [facts, setFacts] = useState<BrandFacts>(loadFacts);
  const [saved, setSaved] = useState(false);

  const brands = useMemo(() => BRAND_LIBRARY || [], []);
  const xeaosBrand = useMemo(() => (brands.find((b: BrandProfile) => b.tag === "XEAOS") || null), [brands]);

  const save = () => {
    try {
      localStorage.setItem(FACTS_KEY, JSON.stringify(facts));
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch { /* ignore */ }
  };

  const reset = () => {
    setFacts(DEFAULT_FACTS);
    try { localStorage.removeItem(FACTS_KEY); } catch { /* ignore */ }
  };

  // P1-3 导出：把品牌事实卡下载为 JSON（便于备份/迁移/多端同步）
  const exportFacts = () => {
    try {
      const blob = new Blob([JSON.stringify(facts, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "xeaos_brand_facts.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  // P1-3 导入：从 JSON 文件恢复品牌事实卡
  const importFacts = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result || "{}"));
        const merged = { ...DEFAULT_FACTS, ...parsed };
        setFacts(merged);
        try { localStorage.setItem(FACTS_KEY, JSON.stringify(merged)); } catch { /* ignore */ }
        setSaved(true);
        setTimeout(() => setSaved(false), 1500);
      } catch {
        alert("导入失败：文件不是合法的 JSON 品牌事实卡");
      }
    };
    reader.readAsText(file);
  };

  const field = (key: keyof BrandFacts, label: string, placeholder: string, textarea = false) => (
    <div>
      <label className="block text-[9px] font-mono text-gray-500 mb-0.5">{label}</label>
      {textarea ? (
        <textarea
          value={facts[key]}
          onChange={(e) => setFacts({ ...facts, [key]: e.target.value })}
          rows={3}
          placeholder={placeholder}
          className="w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-cyan-500/40 resize-y font-mono"
        />
      ) : (
        <input
          value={facts[key]}
          onChange={(e) => setFacts({ ...facts, [key]: e.target.value })}
          placeholder={placeholder}
          className="w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-cyan-500/40 font-mono"
        />
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      {/* 品牌事实卡（可编辑） */}
      <div className="rounded-lg border border-cyan-500/15 bg-cyan-500/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <BookMarked size={11} className="text-cyan-400" />
          <span className="text-[10px] font-mono text-gray-300">XEAOS 品牌事实卡</span>
          <span className="text-[8px] font-mono text-gray-600">（Schema 部署与 GEO 内容共用此事实库）</span>
          <div className="ml-auto flex items-center gap-1.5">
            <button
              onClick={save}
              className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-white bg-cyan-600/80 hover:bg-cyan-600 transition"
            >
              <Save size={9} /> {saved ? "已保存 ✓" : "保存"}
            </button>
            <button
              onClick={exportFacts}
              className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/10 transition"
            >
              <Download size={9} /> 导出
            </button>
            <label className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/10 transition cursor-pointer">
              <Upload size={9} /> 导入
              <input
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) importFacts(f);
                  e.target.value = "";
                }}
              />
            </label>
            <button
              onClick={reset}
              className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-gray-400 border border-white/[0.08] hover:text-white"
            >
              <RotateCcw size={9} /> 重置
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {field("companyFullName", "公司全称", "湖南小舍科技有限公司")}
          {field("brandZh", "中文品牌", "小舍AI")}
          {field("brandEn", "英文品牌", "XEAOS")}
          {field("productNames", "产品名（逗号分隔）", "XEAOS 企业AI操作系统 / GEO+DSO 获客平台…")}
        </div>
        {field("sellingPoints", "核心卖点（每行一条）", "差异化卖点，AI 可引用的独特优势", true)}
        {field("evidenceChain", "证据链 / 权威信源", "官网 / 系统 / 新闻 / 知识库 URL", true)}
        {field("externalProfile", "对外统一口径", "品牌定义 + 竞品策略", true)}
      </div>

      {/* 9 大品牌画像总览 */}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="text-[10px] font-mono text-gray-400 mb-2">品牌画像库（BrandLibraryRuntime · 只读）</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
          {brands.map((b: BrandProfile) => (
            <div key={b.tag} className="rounded border border-white/[0.04] bg-white/[0.01] p-1.5">
              <div className="text-[10px] font-bold text-white">{b.name}</div>
              <div className="text-[8px] font-mono text-gray-500 mt-0.5 truncate">{b.positioning}</div>
              <div className="text-[8px] font-mono text-cyan-400/70 mt-0.5">受众：{b.targetAudience}</div>
            </div>
          ))}
        </div>
        {xeaosBrand && (
          <p className="text-[8px] font-mono text-gray-600 mt-2">
            XEAOS 品牌调性：{xeaosBrand.voice?.tone} · 风格 {xeaosBrand.voice?.style} · 内容支柱 {xeaosBrand.contentPillars?.join(" / ")}
          </p>
        )}
      </div>

      {/* P1-3 覆盖范围：说明此事实库被哪些模块消费 */}
      <div className="rounded border border-white/[0.04] bg-white/[0.01] p-2">
        <div className="text-[8px] font-mono text-gray-500 mb-1">本事实库覆盖范围（编辑后即时生效）</div>
        <div className="flex flex-wrap gap-1">
          {["Schema 一键部署（Organization/Product/Article）", "GEO 内容方向生成", "AI 可见性品牌词提取", "DSO 线索话术", "对外统一口径"].map((t) => (
            <span key={t} className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-300/80 border border-cyan-500/20">
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
